import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Plus, Trash2, CheckCircle, Building2 } from 'lucide-react';
import MeridianLogo from '../../components/MeridianLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
  ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
  ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
  ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

const PROVIDER_TYPES = [
  'Ambulatory Surgical Center',
  'Surgery Center',
  'Specialty Clinic',
  'Hospital',
  'Medical Center',
  'Other',
];

const PROVIDER_CATEGORIES = [
  ['surgical',  'Surgical / General'],
  ['elective',  'Elective / Cosmetic'],
  ['dental',    'Dental'],
  ['fertility', 'Fertility'],
  ['medspa',    'Med Spa'],
  ['bariatric', 'Bariatric'],
  ['vision',    'Vision / Lasik'],
];

const EMPTY_PROCEDURE = { cpt: '', name: '', cash_price: '', includes: '' };

const STEPS = ['Practice Info', 'Contact & About', 'Procedures & Pricing', 'Review & Submit'];

function FieldLabel({ children, required }) {
  return (
    <label className="block text-sm font-medium text-stone-700 mb-1.5">
      {children}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );
}

function Input({ className = '', ...props }) {
  return (
    <input
      className={`w-full px-4 py-3 border border-[#A8B4B8] bg-white text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors ${className}`}
      {...props}
    />
  );
}

function Select({ children, className = '', ...props }) {
  return (
    <select
      className={`w-full px-4 py-3 border border-[#A8B4B8] bg-white text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors ${className}`}
      {...props}
    >
      {children}
    </select>
  );
}

function Textarea({ className = '', ...props }) {
  return (
    <textarea
      rows={4}
      className={`w-full px-4 py-3 border border-[#A8B4B8] bg-white text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors resize-none ${className}`}
      {...props}
    />
  );
}

function StepIndicator({ current }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {STEPS.map((label, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold
                ${i < current ? 'bg-[#0D3D4A] text-white'
                  : i === current ? 'bg-[#0D3D4A] text-white ring-2 ring-[#0D3D4A]/30'
                  : 'bg-stone-200 text-stone-500'}`}
            >
              {i < current ? <CheckCircle size={12} /> : i + 1}
            </div>
            <span className={`text-xs font-medium hidden sm:block ${i === current ? 'text-[#0D3D4A]' : 'text-stone-400'}`}>
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`h-px w-6 sm:w-12 ${i < current ? 'bg-[#0D3D4A]' : 'bg-stone-200'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Step 1: Practice Info ────────────────────────────────────────────────────
function Step1({ data, onChange }) {
  return (
    <div className="space-y-5">
      <div>
        <FieldLabel required>Provider / Facility Name</FieldLabel>
        <Input
          value={data.name}
          onChange={e => onChange('name', e.target.value)}
          placeholder="e.g. Oklahoma Surgical Center"
          required
        />
      </div>
      <div>
        <FieldLabel required>Provider Type</FieldLabel>
        <Select value={data.type} onChange={e => onChange('type', e.target.value)} required>
          <option value="">Select a type…</option>
          {PROVIDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </Select>
      </div>
      <div>
        <FieldLabel required>Category</FieldLabel>
        <Select value={data.category} onChange={e => onChange('category', e.target.value)} required>
          {PROVIDER_CATEGORIES.map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </Select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel required>City</FieldLabel>
          <Input
            value={data.city}
            onChange={e => onChange('city', e.target.value)}
            placeholder="e.g. Tulsa"
            required
          />
        </div>
        <div>
          <FieldLabel required>State</FieldLabel>
          <Select value={data.state} onChange={e => onChange('state', e.target.value)} required>
            <option value="">Select…</option>
            {US_STATES.map(([code, name]) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </Select>
        </div>
      </div>
      <div>
        <FieldLabel required>Street Address</FieldLabel>
        <Input
          value={data.address}
          onChange={e => onChange('address', e.target.value)}
          placeholder="e.g. 123 Main St, Suite 100"
          required
        />
      </div>
      <div>
        <FieldLabel required>Phone</FieldLabel>
        <Input
          type="tel"
          value={data.phone}
          onChange={e => onChange('phone', e.target.value)}
          placeholder="(555) 000-0000"
          required
        />
      </div>
      <div>
        <FieldLabel>Website</FieldLabel>
        <Input
          type="url"
          value={data.website}
          onChange={e => onChange('website', e.target.value)}
          placeholder="https://yourfacility.com"
        />
      </div>
    </div>
  );
}

// ─── Step 2: Contact & About ──────────────────────────────────────────────────
function Step2({ data, onChange }) {
  return (
    <div className="space-y-5">
      <div>
        <FieldLabel required>Contact Name</FieldLabel>
        <Input
          value={data.contact_name}
          onChange={e => onChange('contact_name', e.target.value)}
          placeholder="e.g. Jane Smith"
          required
        />
      </div>
      <div>
        <FieldLabel required>Contact Email</FieldLabel>
        <Input
          type="email"
          value={data.email}
          onChange={e => onChange('email', e.target.value)}
          placeholder="admin@yourfacility.com"
          required
        />
      </div>
      <div>
        <FieldLabel>About Your Facility</FieldLabel>
        <Textarea
          value={data.about}
          onChange={e => onChange('about', e.target.value)}
          placeholder="Briefly describe your facility, specialties, and what makes your pricing transparent…"
        />
      </div>
      <div>
        <FieldLabel>Accreditation</FieldLabel>
        <Input
          value={data.accreditation}
          onChange={e => onChange('accreditation', e.target.value)}
          placeholder="e.g. AAAHC, Joint Commission"
        />
      </div>
      <div>
        <FieldLabel>Specialties <span className="text-stone-400 font-normal">(comma-separated)</span></FieldLabel>
        <Input
          value={data.specialties_raw}
          onChange={e => onChange('specialties_raw', e.target.value)}
          placeholder="e.g. Orthopedics, General Surgery, Ophthalmology"
        />
      </div>
      <div className="flex gap-6 pt-1">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={data.telehealth}
            onChange={e => onChange('telehealth', e.target.checked)}
            className="w-4 h-4 accent-[#0D3D4A]"
          />
          <span className="text-sm text-stone-700">Telehealth available</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={data.cash_pay_only}
            onChange={e => onChange('cash_pay_only', e.target.checked)}
            className="w-4 h-4 accent-[#0D3D4A]"
          />
          <span className="text-sm text-stone-700">Cash pay only</span>
        </label>
      </div>
    </div>
  );
}

// ─── Step 3: Procedures & Pricing ────────────────────────────────────────────
function Step3({ procedures, onUpdate, onAdd, onRemove }) {
  return (
    <div>
      <p className="text-sm text-stone-500 mb-4">
        List your transparent cash prices. Leave rows blank to skip them.
      </p>
      <div className="space-y-3">
        {procedures.map((proc, i) => (
          <div key={i} className="border border-[#A8B4B8] bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Procedure {i + 1}</span>
              {procedures.length > 1 && (
                <button type="button" onClick={() => onRemove(i)} className="text-stone-400 hover:text-red-500 transition-colors">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1">CPT Code</label>
                <Input
                  value={proc.cpt}
                  onChange={e => onUpdate(i, 'cpt', e.target.value)}
                  placeholder="e.g. 27447"
                  className="py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1">Cash Price ($)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={proc.cash_price}
                  onChange={e => onUpdate(i, 'cash_price', e.target.value)}
                  placeholder="e.g. 8500"
                  className="py-2 text-sm"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-stone-600 mb-1">Procedure Name</label>
                <Input
                  value={proc.name}
                  onChange={e => onUpdate(i, 'name', e.target.value)}
                  placeholder="e.g. Total Knee Replacement"
                  className="py-2 text-sm"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-stone-600 mb-1">What's Included</label>
                <Input
                  value={proc.includes}
                  onChange={e => onUpdate(i, 'includes', e.target.value)}
                  placeholder="e.g. Surgeon fee, anesthesia, facility, 90-day follow-ups"
                  className="py-2 text-sm"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={onAdd}
        className="mt-3 flex items-center gap-2 text-sm text-[#0D3D4A] font-medium hover:text-[#0E4557] transition-colors"
      >
        <Plus size={14} />
        Add Procedure
      </button>
    </div>
  );
}

// ─── Step 4: Review & Submit ──────────────────────────────────────────────────
function ReviewRow({ label, value }) {
  if (!value && value !== false) return null;
  return (
    <div className="flex justify-between py-2 border-b border-stone-100 last:border-0">
      <span className="text-sm text-stone-500">{label}</span>
      <span className="text-sm text-stone-800 font-medium text-right max-w-[60%]">{String(value)}</span>
    </div>
  );
}

function Step4({ data, procedures }) {
  const filledProcs = procedures.filter(p => p.name || p.cpt);
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-widest text-[#0D3D4A] mb-3">Practice Info</h3>
        <div className="bg-white border border-[#A8B4B8] p-4">
          <ReviewRow label="Name" value={data.name} />
          <ReviewRow label="Type" value={data.type} />
          <ReviewRow label="Location" value={`${data.city}, ${data.state}`} />
          <ReviewRow label="Address" value={data.address} />
          <ReviewRow label="Phone" value={data.phone} />
          <ReviewRow label="Website" value={data.website} />
        </div>
      </div>
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-widest text-[#0D3D4A] mb-3">Contact & About</h3>
        <div className="bg-white border border-[#A8B4B8] p-4">
          <ReviewRow label="Contact" value={data.contact_name} />
          <ReviewRow label="Email" value={data.email} />
          <ReviewRow label="Accreditation" value={data.accreditation} />
          <ReviewRow label="Telehealth" value={data.telehealth ? 'Yes' : undefined} />
          <ReviewRow label="Cash Pay Only" value={data.cash_pay_only ? 'Yes' : undefined} />
          <ReviewRow label="Specialties" value={data.specialties_raw} />
          {data.about && (
            <div className="pt-2">
              <span className="text-xs text-stone-400 uppercase tracking-wider">About</span>
              <p className="text-sm text-stone-700 mt-1 leading-relaxed">{data.about}</p>
            </div>
          )}
        </div>
      </div>
      {filledProcs.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-[#0D3D4A] mb-3">
            Procedures ({filledProcs.length})
          </h3>
          <div className="bg-white border border-[#A8B4B8] divide-y divide-stone-100">
            {filledProcs.map((p, i) => (
              <div key={i} className="p-4">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-stone-800">{p.name || '—'}</span>
                  {p.cash_price && (
                    <span className="text-sm font-semibold text-[#0D3D4A]">
                      ${Number(p.cash_price).toLocaleString()}
                    </span>
                  )}
                </div>
                {p.cpt && <span className="text-xs text-stone-400">CPT {p.cpt}</span>}
                {p.includes && <p className="text-xs text-stone-500 mt-1">{p.includes}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="bg-[#0D3D4A]/5 border border-[#0D3D4A]/20 p-4 text-sm text-stone-600 leading-relaxed">
        By submitting, you confirm that the information above is accurate. Your listing will be reviewed
        within 1–2 business days before going live on Meridian.
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ProviderSelfRegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    name: '', type: '', city: '', state: '', address: '',
    phone: '', website: '', email: '', contact_name: '',
    about: '', accreditation: '', specialties_raw: '',
    telehealth: false, cash_pay_only: false,
    category: 'surgical',
  });

  const [procedures, setProcedures] = useState([
    { ...EMPTY_PROCEDURE },
    { ...EMPTY_PROCEDURE },
    { ...EMPTY_PROCEDURE },
  ]);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const updateProcedure = (i, key, value) =>
    setProcedures(prev => prev.map((p, idx) => idx === i ? { ...p, [key]: value } : p));

  const addProcedure = () => setProcedures(prev => [...prev, { ...EMPTY_PROCEDURE }]);
  const removeProcedure = (i) => setProcedures(prev => prev.filter((_, idx) => idx !== i));

  const validateStep = () => {
    if (step === 0) {
      if (!form.name.trim()) return 'Facility name is required.';
      if (!form.type) return 'Provider type is required.';
      if (!form.city.trim()) return 'City is required.';
      if (!form.state) return 'State is required.';
      if (!form.address.trim()) return 'Address is required.';
      if (!form.phone.trim()) return 'Phone is required.';
    }
    if (step === 1) {
      if (!form.contact_name.trim()) return 'Contact name is required.';
      if (!form.email.trim()) return 'Contact email is required.';
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError('');
    setStep(s => s + 1);
  };

  const back = () => { setError(''); setStep(s => s - 1); };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        type: form.type,
        city: form.city,
        state: form.state,
        address: form.address,
        phone: form.phone,
        website: form.website,
        email: form.email,
        contact_name: form.contact_name,
        about: form.about,
        accreditation: form.accreditation,
        telehealth: form.telehealth,
        cash_pay_only: form.cash_pay_only,
        category: form.category,
        specialties: form.specialties_raw
          ? form.specialties_raw.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        procedures: procedures
          .filter(p => p.name || p.cpt)
          .map(p => ({
            cpt: p.cpt,
            name: p.name,
            cash_price: parseFloat(p.cash_price) || 0,
            includes: p.includes,
          })),
      };

      const res = await axios.post(`${API}/providers/self-register`, payload);
      setResult(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
          ? detail.map(d => d.msg).join(', ')
          : 'Submission failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Success screen ──────────────────────────────────────────────────────────
  if (result) {
    return (
      <div className="min-h-screen bg-[#F0EEE9] flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-[#0D3D4A]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={32} className="text-[#0D3D4A]" />
          </div>
          <h1 className="text-3xl font-medium text-stone-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Listing submitted!
          </h1>
          <p className="text-stone-500 mb-2">
            Your listing is under review and will go live within 1–2 business days. A confirmation has
            been sent to <strong>{form.email}</strong>.
          </p>
          <p className="text-xs text-stone-400 font-mono mt-4">Provider ID: {result.provider_id}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-[#0D3D4A] text-white text-sm font-medium hover:bg-[#0E4557] transition-colors"
            >
              Back to Meridian
            </button>
            <Link
              to="/provider-portal/login"
              className="px-6 py-3 border border-[#0D3D4A] text-[#0D3D4A] text-sm font-medium hover:bg-[#0D3D4A]/5 transition-colors text-center"
            >
              Provider Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F0EEE9] flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-2/5 bg-[#0D3D4A] flex-col justify-between p-16">
        <Link to="/"><MeridianLogo variant="dark" /></Link>
        <div>
          <div className="w-12 h-12 bg-[#0A3340] flex items-center justify-center mb-8">
            <Building2 size={22} className="text-white" />
          </div>
          <h2 className="text-4xl font-medium text-white mb-6 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            List your practice on Meridian
          </h2>
          <p className="text-[#A8B4B8] text-lg mb-8 leading-relaxed">
            Connect with patients actively searching for transparent cash-pay pricing.
            No hidden fees — patients see exactly what they'll pay.
          </p>
          <div className="space-y-4">
            {[
              'Post your transparent cash prices',
              'Receive qualified patient leads',
              'No insurance hassle — direct-pay patients',
              'Live within 1–2 business days',
            ].map(item => (
              <div key={item} className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={16} className="text-[#A8B4B8]" />
                </div>
                <span className="text-[#A8B4B8] text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-[#F0EEE9]/40 text-xs">
          Already a partner?{' '}
          <Link to="/provider-portal/login" className="text-[#A8B4B8] hover:text-[#F0EEE9]/90">
            Sign in &rarr;
          </Link>
        </p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-start justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-lg py-8">
          {/* Mobile logo */}
          <div className="lg:hidden mb-6">
            <Link to="/"><MeridianLogo variant="light" /></Link>
          </div>

          <div className="mb-6">
            <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-2">For Providers</p>
            <h1 className="text-2xl font-medium text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {STEPS[step]}
            </h1>
          </div>

          <StepIndicator current={step} />

          {step === 0 && <Step1 data={form} onChange={setField} />}
          {step === 1 && <Step2 data={form} onChange={setField} />}
          {step === 2 && (
            <Step3
              procedures={procedures}
              onUpdate={updateProcedure}
              onAdd={addProcedure}
              onRemove={removeProcedure}
            />
          )}
          {step === 3 && <Step4 data={form} procedures={procedures} />}

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mt-8 flex justify-between">
            {step > 0 ? (
              <button
                type="button"
                onClick={back}
                className="flex items-center gap-2 text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors"
              >
                <ArrowLeft size={14} /> Back
              </button>
            ) : (
              <span />
            )}

            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={next}
                className="flex items-center gap-2 bg-[#0D3D4A] text-white px-6 py-3 text-sm font-semibold hover:bg-[#0E4557] transition-colors"
              >
                Continue <ArrowRight size={14} />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 bg-[#0D3D4A] text-white px-6 py-3 text-sm font-semibold hover:bg-[#0E4557] transition-colors disabled:opacity-60"
              >
                {loading ? 'Submitting…' : 'Submit Listing'}
                {!loading && <ArrowRight size={14} />}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
